import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCountdownSeconds } from '@/deploy/hooks/useCountdown';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { ContainerFilesModal } from '@/deploy/components/ui/ContainerFilesModal';
import { ContainerReleasePanel } from '@/deploy/components/container/ContainerReleasePanel';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useContainersStore } from '@/deploy/stores/containers.store';
import { containerFilesService, type FileModificationRemote } from '@/deploy/services/container-files.service';
import { containersService } from '@/deploy/services/containers.service';
import { MOCK_INSTANCES } from '@/deploy/data/mock';
import { ENVIRONMENTS } from '@/deploy/types';
import type { ArtifactKind } from '@/deploy/types';
import { formatRelative } from '@/deploy/utils/format';
import { can } from '@/deploy/utils/permissions';
import styles from './Containers.module.scss';

/** Paramètre d'URL de l'interrupteur « Afficher les archivés » (gardé au retour d'une fiche archivée). */
const ARCHIVES_PARAM = 'archives';

const KIND_LABEL: Record<ArtifactKind, string> = {
  mod: 'Mods',
  module: 'Modules',
  plugin: 'Plugins',
  config: 'Configs',
  lib: 'Libs',
};

export function Containers() {
  const params = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());

  const containers = useContainersStore((s) => s.containers);
  const loading = useContainersStore((s) => s.loading);
  const syncing = useContainersStore((s) => s.syncing);
  const lastSync = useContainersStore((s) => s.lastSync);
  const loadedProjectId = useContainersStore((s) => s.loadedProjectId);
  const load = useContainersStore((s) => s.load);
  const syncAll = useContainersStore((s) => s.syncAll);
  const syncOne = useContainersStore((s) => s.syncOne);
  const cooldownAllUntil = useContainersStore((s) => s.cooldownAllUntil);
  const cooldownByCode = useContainersStore((s) => s.cooldownByCode);
  const refreshCooldown = useContainersStore((s) => s.refreshCooldown);
  const archived = useContainersStore((s) => s.archived);
  const archivedLoadedProjectId = useContainersStore((s) => s.archivedLoadedProjectId);
  const loadArchived = useContainersStore((s) => s.loadArchived);
  const archiveContainer = useContainersStore((s) => s.archive);
  const unarchiveContainer = useContainersStore((s) => s.unarchive);

  // Archiver et désarchiver : même permission que les promotions en prod (le CICD l'exige aussi)
  const user = useSessionStore((s) => s.user);
  const canArchive = can(user, 'cicd.deploy.prod');
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get(ARCHIVES_PARAM) === '1';
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [unarchiving, setUnarchiving] = useState<string | null>(null);
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  const cooldownAllSec = useCountdownSeconds(cooldownAllUntil);
  const cooldownOneSec = useCountdownSeconds(params.code ? (cooldownByCode[params.code] ?? 0) : 0);

  // Load on mount + when projectId changes
  useEffect(() => {
    if (!project.id) return;
    void load(project.id);
    void loadArchived(project.id);
    void refreshCooldown(project.id);
  }, [project.id, load, loadArchived, refreshCooldown]);

  const toggleArchived = (on: boolean) => {
    setUnarchiveError(null);
    const next = new URLSearchParams(searchParams);
    if (on) next.set(ARCHIVES_PARAM, '1'); else next.delete(ARCHIVES_PARAM);
    setSearchParams(next, { replace: true });
  };

  const confirmArchive = async (code: string) => {
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      await archiveContainer(project.id, code);
      setArchiveOpen(false);
    } catch (e) {
      // Ex. release en cours : le CICD dit laquelle et quoi faire
      setArchiveError(e instanceof Error ? e.message : 'Archivage refusé');
    } finally {
      setArchiveBusy(false);
    }
  };

  const unarchiveOne = async (code: string) => {
    setUnarchiving(code);
    setUnarchiveError(null);
    try {
      await unarchiveContainer(project.id, code);
    } catch (e) {
      setUnarchiveError(e instanceof Error ? e.message : 'Désarchivage refusé');
    } finally {
      setUnarchiving(null);
    }
  };

  const [kindFilter, setKindFilter] = useState<ArtifactKind | 'all'>('all');
  const [ctnTab, setCtnTab] = useState<'artifacts' | 'files' | 'instances' | 'releases'>('artifacts');
  // L'onglet Fichiers est monté au 1er accès puis gardé monté (masqué) pour préserver
  // l'édition en cours entre onglets, sans charger l'arbre tant qu'on ne l'ouvre pas.
  const [filesMounted, setFilesMounted] = useState(false);
  const [artifactSearch, setArtifactSearch] = useState('');
  // Signal de refresh vers le panneau Fichiers intégré (bumpé après un revert côté parent).
  const [filesRefresh, setFilesRefresh] = useState(0);
  const [mods, setMods] = useState<FileModificationRemote[]>([]);
  const [modsLoading, setModsLoading] = useState(false);

  // Reset SYNCHRONE de l'état du détail au changement de container. Le composant ne remonte
  // PAS entre deux containers (même route, cf. commentaire plus bas) : on réinitialise pendant
  // le render (pattern React « adjusting state on prop change ») pour ne jamais peindre une
  // frame avec l'état de l'ancien container (onglet actif, mods, recherche, filtre…).
  const [detailCode, setDetailCode] = useState<string | undefined>(params.code);
  if (params.code && params.code !== detailCode) {
    setDetailCode(params.code);
    setCtnTab('artifacts');
    setFilesMounted(false);
    setArtifactSearch('');
    setKindFilter('all');
    setMods([]);
    setModsLoading(true);
    setArchiveOpen(false);
    setArchiveError(null);
    setUnarchiveError(null);
  }

  // Charge les mods du container détail (le reset ci-dessus a déjà vidé l'ancien état).
  useEffect(() => {
    if (!params.code || !project.id) return;
    setModsLoading(true);
    containerFilesService.listMods(project.id, params.code)
      .then(setMods)
      .catch(() => setMods([]))
      .finally(() => setModsLoading(false));
  }, [params.code, project.id]);

  // IMPORTANT — tous les hooks doivent être appelés AVANT toute return conditionnelle.
  // Sinon, naviguer entre /containers et /containers/:code change le nombre de hooks
  // entre deux renders → React crash silencieux → page blanche.
  const projectContainers = useMemo(
    () => containers.filter((c) => c.projectId === project.id),
    [containers, project.id],
  );
  const projectArchived = useMemo(
    () => archived.filter((c) => c.projectId === project.id),
    [archived, project.id],
  );

  if (params.code) {
    // Tant qu'on n'a pas chargé pour ce projet, on attend (évite "Container inconnu" transitoire)
    if (loadedProjectId !== project.id) {
      return <div style={{ padding: 24 }}>Chargement…</div>;
    }
    // Fiche d'un actif, ou d'un archivé (liste chargée à part)
    const container = containers.find((c) => c.code === params.code)
      ?? projectArchived.find((c) => c.code === params.code);
    if (!container) {
      if (archivedLoadedProjectId !== project.id) return <div style={{ padding: 24 }}>Chargement…</div>;
      return <div style={{ padding: 24 }}>Container inconnu</div>;
    }
    const isArchived = container.archived === true;
    const instances = MOCK_INSTANCES.filter((i) => i.containerId === container.id);
    const artQuery = artifactSearch.trim().toLowerCase();
    const releases = container.releases.filter((r) =>
      (kindFilter === 'all' || r.kind === kindFilter) &&
      (!artQuery || r.name.toLowerCase().includes(artQuery) || r.repository.toLowerCase().includes(artQuery)),
    );

    const refreshMods = async () => {
      try {
        const list = await containerFilesService.listMods(project.id, container.code);
        setMods(list);
      } catch { /* noop */ }
    };

    const revertOne = async (path: string) => {
      try {
        await containerFilesService.revertOne(project.id, container.code, path);
        await refreshMods();
        setFilesRefresh((n) => n + 1);
      } catch (e) {
        alert('Revert échoué: ' + (e instanceof Error ? e.message : 'inconnu'));
      }
    };

    const revertAll = async () => {
      if (!confirm(`Annuler les ${mods.length} modifications en attente ?`)) return;
      try {
        await containerFilesService.revertAll(project.id, container.code);
        await refreshMods();
        setFilesRefresh((n) => n + 1);
      } catch (e) {
        alert('Revert all échoué: ' + (e instanceof Error ? e.message : 'inconnu'));
      }
    };

    return (
      <div className={styles.page}>
        <button className={styles.back} onClick={() => navigate(isArchived || showArchived
          ? `/deploiement/containers?${ARCHIVES_PARAM}=1` : '/deploiement/containers')}>
          <Icon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> Tous les containers
        </button>

        <PageHeader
          eyebrow={`Container · ${container.managementMode === 'nexus-dynamic' ? 'dynamique (Nexus)' : 'individuel (Pterodactyl)'}`}
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              <span className={styles.chip} style={{ background: container.color }}>
                <Icon name="package" size={20} />
              </span>
              {container.displayName}
              <code className={styles.version}>{container.containerVersion ?? '?'}</code>
              {isArchived && <Badge tone="muted"><Icon name="archive" size={11} /> Archivé</Badge>}
            </span>
          }
          subtitle={container.repository}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              {canArchive && (isArchived ? (
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<Icon name="archive" size={14} />}
                  onClick={() => void unarchiveOne(container.code)}
                  loading={unarchiving === container.code}
                  title="Le remettre dans la liste et dans le processus de release, tel qu'il était"
                >
                  Désarchiver
                </Button>
              ) : (
                <Button
                  variant="softWarning"
                  size="md"
                  leftIcon={<Icon name="archive" size={14} />}
                  onClick={() => { setArchiveError(null); setArchiveOpen(true); }}
                  title="Retirer ce container de la liste et du processus de release (réversible)"
                >
                  Archiver
                </Button>
              ))}
              <Button
                variant="ghost"
                size="md"
                leftIcon={<Icon name="refresh" size={14} />}
                onClick={() => syncOne(project.id, container.code)}
                loading={syncing}
                disabled={syncing || cooldownOneSec > 0 || isArchived}
                title={isArchived
                  ? 'Container archivé : désarchive-le pour le synchroniser'
                  : cooldownOneSec > 0
                    ? `Cooldown : ${cooldownOneSec}s avant le prochain sync`
                    : "Sync GitHub Releases pour ce container"}
              >
                {cooldownOneSec > 0 ? `Sync (${cooldownOneSec}s)` : 'Sync'}
              </Button>
              <a href={`https://github.com/${container.repository}`} target="_blank" rel="noreferrer">
                <Button variant="secondary" size="md" leftIcon={<Icon name="external" size={14} />}>GitHub</Button>
              </a>
            </div>
          }
        />

        {isArchived && (
          <div className={styles.archivedBanner} role="status">
            <Icon name="archive" size={16} />
            <div>
              <strong>
                Container archivé{container.archivedAt ? ` ${formatRelative(container.archivedAt)}` : ''}
                {container.archivedByName ? ` par ${container.archivedByName}` : ''}
              </strong>
              <p>
                Il n'apparaît plus dans la liste, n'est plus synchronisé ni proposé aux releases et aux promotions,
                et ses fichiers sont en lecture seule. Son historique est gardé : « Désarchiver » le remet tel qu'il était.
              </p>
            </div>
          </div>
        )}
        {unarchiveError && (
          <div className={styles.archiveError}><Icon name="warn" size={12} /> {unarchiveError}</div>
        )}

        <Modal
          open={archiveOpen}
          onClose={archiveBusy ? () => {} : () => setArchiveOpen(false)}
          title={`Archiver ${container.displayName} ?`}
          subtitle={container.repository}
          width="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setArchiveOpen(false)} disabled={archiveBusy}>Annuler</Button>
            <Button
              variant="primary"
              leftIcon={<Icon name="archive" size={14} />}
              onClick={() => void confirmArchive(container.code)}
              loading={archiveBusy}
            >
              Archiver
            </Button>
          </>}
        >
          <ul className={styles.archiveEffects}>
            <li><strong>Il disparaît de la liste</strong> des containers. L'interrupteur « Afficher les archivés » le montre encore.</li>
            <li><strong>Il n'est plus synchronisé</strong> avec GitHub, ni proposé aux releases et aux promotions.</li>
            <li><strong>Son historique est gardé</strong> : releases, artefacts et fichiers restent consultables.</li>
            <li><strong>C'est réversible</strong> : « Désarchiver » le remet tel qu'il était.</li>
          </ul>
          {archiveError && (
            <div className={styles.archiveError}><Icon name="warn" size={12} /> {archiveError}</div>
          )}
        </Modal>

        <DockerImageRow
          projectId={project.id}
          containerCode={container.code}
          currentTemplate={container.dockerImage ?? `harbor.be.recube.gg/paladium-bedrock/${container.code}:{version}`}
          containerVersion={container.containerVersion ?? null}
        />

        <div className={styles.tabs}>
          <button
            className={[styles.tab, ctnTab === 'artifacts' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setCtnTab('artifacts')}
          >
            <Icon name="git-commit" size={14} /> Artefacts
            <Badge tone={ctnTab === 'artifacts' ? 'primary' : 'muted'}>{container.releases.length}</Badge>
          </button>
          <button
            className={[styles.tab, ctnTab === 'files' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => { setCtnTab('files'); setFilesMounted(true); }}
          >
            <Icon name="layers" size={14} /> Fichiers
            {mods.length > 0 && <Badge tone="warning">{mods.length}</Badge>}
          </button>
          <button
            className={[styles.tab, ctnTab === 'instances' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setCtnTab('instances')}
          >
            <Icon name="server" size={14} /> Instances
            <Badge tone={ctnTab === 'instances' ? 'primary' : 'muted'}>{instances.length}</Badge>
          </button>
          <button
            className={[styles.tab, ctnTab === 'releases' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setCtnTab('releases')}
          >
            <Icon name="rocket" size={14} /> Releases
          </button>
        </div>

        {ctnTab === 'artifacts' && (
          <section>
            <header className={styles.sectionHeader}>
              <div>
                <h2><Icon name="git-commit" size={18} /> release-version.json</h2>
                <p>{container.releases.length} artefacts GitHub dans ce container</p>
              </div>
              <div className={styles.kindFilters}>
                <label className={styles.artifactSearch}>
                  <Icon name="search" size={13} />
                  <input
                    type="text"
                    placeholder="Rechercher un artefact…"
                    value={artifactSearch}
                    onChange={(e) => setArtifactSearch(e.target.value)}
                  />
                </label>
                <FilterBtn active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>Tous</FilterBtn>
                {(['mod', 'module', 'plugin', 'config', 'lib'] as ArtifactKind[]).map((k) => {
                  const count = container.releases.filter((r) => r.kind === k).length;
                  if (count === 0) return null;
                  return (
                    <FilterBtn key={k} active={kindFilter === k} onClick={() => setKindFilter(k)}>
                      {KIND_LABEL[k]} <span className={styles.kindCount}>{count}</span>
                    </FilterBtn>
                  );
                })}
              </div>
            </header>

            <div className={styles.releases}>
              {releases.length === 0 && (
                <div className={styles.empty}>Aucun artefact ne correspond{artifactSearch.trim() ? ` à « ${artifactSearch.trim()} »` : ''}.</div>
              )}
              {releases.map((r) => {
                const outdated = r.latestAvailable && r.latestAvailable !== r.version;
                return (
                  <div key={r.name} className={styles.release}>
                    <div className={styles.releaseKind} title={r.kind}>
                      <Icon name={r.kind === 'module' ? 'layers' : r.kind === 'config' ? 'settings' : 'zap'} size={14} />
                    </div>
                    <div className={styles.releaseInfo}>
                      <div className={styles.releaseName}>{r.name}</div>
                      <div className={styles.releaseRepo}>{r.repository}</div>
                    </div>
                    <div className={styles.releaseDest}>
                      <Badge tone="muted">{r.dest}</Badge>
                    </div>
                    <div className={styles.releaseVer}>
                      <code>{r.version}</code>
                      {outdated && (
                        <Badge tone="warning">
                          <Icon name="arrow-right" size={9} /> {r.latestAvailable}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {ctnTab === 'instances' && (
          <section>
            <header className={styles.sectionHeader}>
              <div>
                <h2><Icon name="server" size={18} /> Instances en cours</h2>
                <p>{instances.length} serveur{instances.length > 1 ? 's' : ''} Pterodactyl utilisent ce container</p>
              </div>
            </header>
            <div className={styles.instances}>
              {instances.length === 0 && <div className={styles.empty}>Aucune instance n'utilise ce container actuellement.</div>}
              {instances.map((i) => (
                <div key={i.id} className={styles.instance}>
                  <span className={[styles.healthDot, styles[`health_${i.health}`]].join(' ')} />
                  <div className={styles.releaseInfo}>
                    <div className={styles.releaseName}>{i.name}</div>
                    <div className={styles.releaseRepo}>{i.node} · {i.environment}</div>
                  </div>
                  <code className={styles.releaseVer}>{i.runningVersion}</code>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/deploiement/logs?instance=${i.id}`)} leftIcon={<Icon name="eye" size={12} />}>
                    Logs
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {ctnTab === 'releases' && (
          <section>
            <ContainerReleasePanel
              projectId={project.id}
              containerCode={container.code}
              containerVersion={container.containerVersion ?? null}
              artifacts={container.releases}
              archived={isArchived}
            />
          </section>
        )}

        {ctnTab === 'files' && mods.length > 0 && (
          <section>
            <header className={styles.sectionHeader}>
              <div>
                <h2><Icon name="warn" size={18} /> Modifications en attente</h2>
                <p>{mods.length} fichier{mods.length > 1 ? 's' : ''} diverge{mods.length > 1 ? 'nt' : ''} du repo · seront embarqué{mods.length > 1 ? 's' : ''} au prochain déploiement</p>
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} onClick={revertAll}>
                Tout annuler
              </Button>
            </header>
            <div className={styles.fileMods}>
              {mods
                .slice()
                .sort((a, b) => a.path.localeCompare(b.path))
                .map((m) => (
                  <div key={m.path} className={[styles.fileMod, styles[`mod_${m.type}`]].join(' ')}>
                    <span className={[styles.modSign, styles[`sign_${m.type}`]].join(' ')}>
                      {m.type === 'added' ? '+' : m.type === 'modified' ? '~' : '−'}
                    </span>
                    <div className={styles.modInfo}>
                      <code className={styles.modPath}>{m.path}</code>
                      <div className={styles.modMeta}>
                        <Badge tone={m.type === 'added' ? 'success' : m.type === 'modified' ? 'warning' : 'danger'}>
                          {m.type === 'added' ? 'ajouté' : m.type === 'modified' ? 'modifié' : 'supprimé'}
                        </Badge>
                        <span>{m.kind}</span>
                        {m.size && <><span>·</span><span>{m.size}</span></>}
                        <span>·</span>
                        <span>par {m.by} · {formatRelative(m.at)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Icon name="refresh" size={11} />}
                      onClick={() => revertOne(m.path)}
                    >
                      Annuler
                    </Button>
                  </div>
                ))}
            </div>
          </section>
        )}
        {ctnTab === 'files' && modsLoading && mods.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Chargement modifs…</div>}

        {/* Onglet Fichiers : monté au 1er accès puis gardé monté (masqué) pour préserver l'édition */}
        {filesMounted && (
        <section style={{ display: ctnTab === 'files' ? undefined : 'none' }}>
          <ContainerFilesModal
            key={container.code}
            embedded
            open
            refreshSignal={filesRefresh}
            onClose={() => {}}
            projectId={project.id}
            containerCode={container.code}
            containerColor={container.color}
            containerName={container.displayName}
            containerRepo={container.repository}
            containerVersion={container.containerVersion ?? '?'}
            onModsChanged={refreshMods}
          />
        </section>
        )}
      </div>
    );
  }

  // --- Liste des containers ---
  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Containers"
        title="Types de serveurs Minecraft"
        subtitle={`${projectContainers.length} containers Pterodactyl · chacun avec son release-version.json`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Toggle
              checked={showArchived}
              onChange={toggleArchived}
              label={`Afficher les archivés (${projectArchived.length})`}
            />
            {lastSync && (
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                Dernier sync · {formatRelative(lastSync.ranAt)}
                {lastSync.ko > 0 && <strong style={{ color: 'var(--color-danger)' }}> · {lastSync.ko} KO</strong>}
              </span>
            )}
            <Button
              variant="primary"
              size="md"
              leftIcon={<Icon name="refresh" size={14} />}
              onClick={() => syncAll(project.id)}
              loading={syncing}
              disabled={syncing || cooldownAllSec > 0}
              title={cooldownAllSec > 0
                ? `Cooldown : ${cooldownAllSec}s avant le prochain sync global`
                : 'Sync GitHub Releases pour tous les containers du projet'}
            >
              {cooldownAllSec > 0 ? `Sync GitHub (${cooldownAllSec}s)` : 'Sync GitHub'}
            </Button>
          </div>
        }
      />

      {loading && projectContainers.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Chargement…
        </div>
      )}

      <div className={styles.grid}>
        {projectContainers.map((c) => {
          const instanceCount = MOCK_INSTANCES.filter((i) => i.containerId === c.id).length;
          const totalPending = ENVIRONMENTS.reduce((acc, e) => acc + (c.pendingBumps?.[e.id] ?? 0), 0);
          const outdatedArtifacts = c.releases.filter((r) => r.latestAvailable && r.latestAvailable !== r.version).length;
          return (
            <article key={c.id} className={styles.card} onClick={() => navigate(`/deploiement/containers/${c.code}`)}>
              <header className={styles.cardHeader}>
                <span className={styles.chipLg} style={{ background: c.color }}>
                  <Icon name="package" size={18} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={styles.cardTitle}>
                    {c.displayName}
                    <code className={styles.cardVer}>{c.containerVersion ?? '—'}</code>
                  </div>
                  <div className={styles.cardRepo}>{c.repository}</div>
                </div>
                <Badge tone={c.managementMode === 'nexus-dynamic' ? 'info' : 'primary'}>
                  {c.managementMode === 'nexus-dynamic' ? 'Nexus' : 'Individuel'}
                </Badge>
              </header>

              <div className={styles.cardStats}>
                <div>
                  <span>{c.releases.length}</span>
                  <em>artefacts</em>
                </div>
                <div>
                  <span>{instanceCount}</span>
                  <em>instances</em>
                </div>
                <div>
                  <span className={totalPending > 0 ? styles.warn : styles.ok}>{totalPending}</span>
                  <em>bumps en attente</em>
                </div>
                <div>
                  <span className={outdatedArtifacts > 0 ? styles.warn : styles.ok}>{outdatedArtifacts}</span>
                  <em>artefacts dispo</em>
                </div>
              </div>

              <footer className={styles.cardFooter}>
                <span>Dernier sync : {c.lastBuildAt ? formatRelative(c.lastBuildAt) : '—'}</span>
                <Icon name="chevron-right" size={14} />
              </footer>
            </article>
          );
        })}
      </div>

      {showArchived && (
        <section className={styles.archivedSection}>
          <header className={styles.sectionHeader}>
            <div>
              <h2><Icon name="archive" size={18} /> Containers archivés</h2>
              <p>
                Hors de la liste, plus synchronisés ni proposés aux releases et aux promotions.
                Leur historique est gardé et « Désarchiver » les remet tels qu'ils étaient.
              </p>
            </div>
          </header>
          {unarchiveError && (
            <div className={styles.archiveError}><Icon name="warn" size={12} /> {unarchiveError}</div>
          )}
          {projectArchived.length === 0 ? (
            <div className={styles.empty}>Aucun container archivé.</div>
          ) : (
            <div className={styles.grid}>
              {projectArchived.map((c) => (
                <article
                  key={c.id}
                  className={[styles.card, styles.cardArchived].join(' ')}
                  onClick={() => navigate(`/deploiement/containers/${c.code}?${ARCHIVES_PARAM}=1`)}
                >
                  <header className={styles.cardHeader}>
                    <span className={styles.chipLg} style={{ background: c.color }}>
                      <Icon name="package" size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={styles.cardTitle}>
                        {c.displayName}
                        <code className={styles.cardVer}>{c.containerVersion ?? '?'}</code>
                      </div>
                      <div className={styles.cardRepo}>{c.repository}</div>
                    </div>
                    <Badge tone="muted"><Icon name="archive" size={10} /> Archivé</Badge>
                  </header>
                  <footer className={styles.cardFooter}>
                    <span>
                      Archivé{c.archivedAt ? ` ${formatRelative(c.archivedAt)}` : ''}
                      {c.archivedByName ? ` par ${c.archivedByName}` : ''}
                    </span>
                    {canArchive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Icon name="archive" size={11} />}
                        loading={unarchiving === c.code}
                        disabled={unarchiving !== null}
                        onClick={(e) => { e.stopPropagation(); void unarchiveOne(c.code); }}
                      >
                        Désarchiver
                      </Button>
                    )}
                  </footer>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

interface FilterBtnProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}
function FilterBtn({ active, onClick, children }: FilterBtnProps) {
  return (
    <button className={[styles.filterBtn, active && styles.filterActive].filter(Boolean).join(' ')} onClick={onClick}>
      {children}
    </button>
  );
}

// ============================================================
// Édition inline de l'image Docker (template) du container
// ============================================================
interface DockerImageRowProps {
  projectId: string;
  containerCode: string;
  currentTemplate: string;
  containerVersion: string | null;
}
function DockerImageRow({ projectId, containerCode, currentTemplate, containerVersion }: DockerImageRowProps) {
  const refresh = useContainersStore((s) => s.refreshOne);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentTemplate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setValue(currentTemplate); }, [currentTemplate]);

  const hasVersionToken = value.includes('{version}') || value.includes('{versionRaw}');
  const previewVersion = containerVersion ?? 'v0.0.1';
  const previewTag = previewVersion.startsWith('v') ? previewVersion.substring(1) : previewVersion;
  const preview = value
    .replace('{version}', previewTag)
    .replace('{versionRaw}', previewVersion);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await containersService.update(projectId, containerCode, { dockerImage: value.trim() });
      await refresh(projectId, containerCode);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{
      padding: 14,
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 8,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="package" size={14} />
        <strong style={{ fontSize: 13 }}>Image Docker publiée par le CI</strong>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          tokens : <code>{'{version}'}</code> (sans v) · <code>{'{versionRaw}'}</code> (avec v)
        </span>
        <div style={{ flex: 1 }} />
        {!editing && (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={11} />}
            onClick={() => setEditing(true)}>
            Modifier
          </Button>
        )}
      </div>

      {editing ? (
        <>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={saving}
            style={{
              fontFamily: 'var(--font-family-mono)', fontSize: 12,
              padding: '6px 10px',
              background: 'var(--color-background-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 6, color: 'var(--color-text)',
            }}
            placeholder="harbor.be.recube.gg/paladium-bedrock/proxy:{version}"
          />
          <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
            <span>→ <code style={{ fontFamily: 'var(--font-family-mono)' }}>{preview}</code></span>
            {!hasVersionToken && <Badge tone="warning">image statique — update-images sautera ce container</Badge>}
          </div>
          {error && <div style={{ color: 'var(--color-danger)', fontSize: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <Button variant="ghost" size="sm" onClick={() => { setValue(currentTemplate); setEditing(false); setError(null); }} disabled={saving}>Annuler</Button>
            <Button variant="primary" size="sm" onClick={() => void save()} disabled={saving || !value.trim()} loading={saving}>
              Sauvegarder
            </Button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <code style={{
            fontFamily: 'var(--font-family-mono)', fontSize: 12,
            background: 'var(--color-background-secondary)',
            padding: '4px 8px', borderRadius: 4,
          }}>{currentTemplate}</code>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>→</span>
          <code style={{
            fontFamily: 'var(--font-family-mono)', fontSize: 12,
            background: 'var(--color-success-bg)', color: 'var(--color-success)',
            padding: '4px 8px', borderRadius: 4,
          }}>{preview}</code>
          {!hasVersionToken && <Badge tone="warning">statique</Badge>}
        </div>
      )}
    </section>
  );
}
